import { memo, useState, useEffect } from 'react';
import Link from 'next/link';
import Head from 'next/head';

import ApproachLocale from 'locale/en/approach.json';
import MiscLocale from 'locale/en/misc.json';
import SitePopup from 'components/site/site-popup';
import SiteFilter from 'components/site/site-filter';
import SiteMarquee from 'components/site/site_marquee';
import SiteSections from 'components/site/site_sections';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import RichTextRenderer from 'components/misc/rich-text-renderer';

import Svg from 'lib/svgs.json';
import Url from 'lib/siteurls';
import LegacyImage from 'next/legacy/image';
import withVisibleHoc from 'components/misc/visible-hoc';
import ModalShime from 'components/modal/shime';
import ModalTaxonomy from 'components/modal/taxonomy';
import ModalDiversity from 'components/modal/diversity';

import cloudinaryLoader from 'lib/image/cloudinary-loader';
import styled, { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import {
  WithFontBody,
  WithFontHead,
  WithSiteLayout,
  WithSiteLayoutPadding,
} from 'styles/commonCss';
import { getContentSection } from 'lib/contentful/get-content-section';
import { getContentField } from 'lib/contentful/get-content-field';
import { createMarkup } from 'lib/utils/create-markup';
import { pageReplaced } from 'lib/utils/page-replaced';
import { getOffsetTop } from 'lib/utils/get-offset-top';
import { isScreenLarge } from 'lib/utils/screen-size';
import { getContentfulDataDirect } from 'lib/contentful/get-contentful-data-direct';
import { SCIENTISTS_QUERY } from 'lib/contentful/scientists-query';
import { getContentFieldClass } from 'lib/contentful/get-content-field-class';
import { mqSmallMin, mqSmallMax } from 'styled/tools/media-queries';

const GlobalStyleApproach = createGlobalStyle`
  .site-content {
    * {
      color: var(--text-primary);
    }
  }

  .site-filter-lg {
    display: none;
  }

  .site-filter {
    .body .list-item {
      &.active {
        display: block;
      }
    }
  }

  &.with-scrolled header {
    opacity: 0;
    @media ${mqLargeMin} {
      opacity: 1;
    }
  }

  [data-mobile-only] {
    display: none !important;
    @media ${mqSmallMax} {
      display: block !important;
    }
  }

  [data-desktop-only] {
    display: block !important;
    @media ${mqSmallMax} {
      display: none !important;
    }
  }

  #modal-taxonomy {
  .legend {
    @media ${mqSmallMin} {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
    }
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 0 0 100px 0;
  }
  .legend .title {
    @media ${mqSmallMin} {
      font-size: 30px;
    }
    font-size: 30px;
    font-weight: 300;
  }
  .legend .list {
    @media ${mqSmallMin} {
      margin: 0;
    }
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: var(--spacing-48) 0 0;
  }
  .legend .list li {
    @media ${mqSmallMin} {
      margin: 0 0 0 var(--spacing-48);
    }
    display: flex;
    align-items: center;
    font-size: var(--font-size-350);
    margin: 0 var(--spacing-24) 0 0;
  }
  .legend .list li:before {
    content: '';
    width: 1rem;
    height: 1rem;
    display: inline-block;
    border-radius: 50%;
    transform: scale(0.9);
    margin: 0 var(--spacing-8) 0 0;
  }
  .legend .list li:nth-child(1):before {
    background: var(--fill-brand);
  }
  .legend .list li:nth-child(2):before {
    background: rgba(70,107,34, 0.5);
  }
  .legend .list li:nth-child(3):before {
    background: rgba(70,107,34, 0.25);
  }

  .taxtable {
    display: block;
    margin: 0 -20px;
    padding: 0 0 1px;
  }
  .taxtable .container {
    width: 100%;
  }

  @media ${mqSmallMin} {
    .taxtable .a {
      display: block;
      margin-bottom: 4rem;
    }
    .taxtable .a .b {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 1rem;
    }
    .taxtable .a .b .c {
      position: relative;
      padding: 0 var(--spacing-24);
    }
    .taxtable span {
      border-radius: 1.25rem;
      min-height: 2.5rem;
      text-align: center;
      display: block;
      background-color: rgba(70,107,34, 0.25);
      font-weight: 500;
      font-size: 12px;
      color: var(--text-primary);

      line-height: calc(2.5rem - 4px);
      padding-top: 4px;
    }
    .taxtable span:empty {
      opacity: 0;
    }
    .taxtable .a .b0 .c span {
      background: var(--fill-brand);
      font-style: italic;
      color: #fff;
      font-size: 14px;
      line-height: 2.5rem;
      padding-top: 0;
    }
    .taxtable .a .b1 {
      margin-top: var(--spacing-48);
    }
    .taxtable .a .b1 .c span:before {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 100%;
      width: 1px;
      height: var(--spacing-48);
      border-right: 1px dashed rgba(0,0,0, 0.5);
    }
    .taxtable .a .b2 {
      margin-top: var(--spacing-48);
    }
    .taxtable .a .b2 .c span:before {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 100%;
      width: 1px;
      height: var(--spacing-48);
      border-right: 1px dashed rgba(0,0,0, 0.5);
    }
    .taxtable .a .b1 .c span {
      background: rgba(70,107,34, 0.5);
      font-size: 14px;
      font-style: italic;
      line-height: 2.5rem;
      padding-top: 0;
    }
    .taxtable .a0 .c {
      width: 20%;
    }
    .taxtable .a0 .b0 .c {
      width: 100%;
    }
    .taxtable .a1 .c {
      width: 25%;
    }
    .taxtable .a1 .b0 .c {
      width: 50%;
    }
    .taxtable .a2 .c {
      width: calc(100% / 3);
    }
  }
  @media ${mqSmallMax} {
    .taxtable .a {
      display: block;
      margin-bottom: 4rem;
    }
    .taxtable .a .b {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: var(--spacing-48);
    }
    .taxtable .a .b .c {
      width: 100%;
      position: relative;
      margin: var(--spacing-8) 0 0;
      padding: 0 var(--spacing-24);
    }
    .taxtable .a .b .c span {
      background-color: rgba(70,107,34, 0.25);
      border-radius: 1.25rem;
      line-height: 2.5rem;
      height: 2.5rem;
      text-align: center;
      display: block;
      font-weight: 400;
      font-size: 14px;
      color: var(--text-primary);
      float: right;
      width: 50%;
      position: relative;
    }
    .taxtable .a .b .c span:before {
      content: '';
      width: 2rem;
      position: absolute;
      right: 100%;
      top: 50%;
      border-bottom: 1px dashed rgba(0,0,0, 0.5);
    }
    .taxtable .a .b .c span:after {
      content: '';
      height: 4.25rem;
      position: absolute;
      left: -2rem;
      bottom: 20px;
      border-left: 1px dashed rgba(0,0,0, 0.5);
    }
    .taxtable .a .b .c0 {
      margin: 0;
    }
    .taxtable .a .b .c0 span {
      transform: translateX(-50%);
      background: rgba(70,107,34, 0.5);
      margin: 0 0 0 50%;
    }
    .taxtable .a .b0 .c0 span {
      transform: translate(0, 0);
      background: var(--fill-brand);
      font-style: italic;
      color: #fff;
      width: 100%;
      margin: 0;
    }
    .taxtable .a .b0 .c0 span:before {
      display: none;
    }
    .taxtable .a .b .c1 {
      margin: var(--spacing-48) 0 0;
    }

    .taxtable .a .b1 .c0 span:after {
      height: 4.25rem;
    }
    .taxtable .a0 .b2 .c0 span:after {
      height: 11rem;
    }
    .taxtable .a0 .b3 .c0 span:after {
      height: 14.25rem;
    }
    .taxtable .a0 .b4 .c0 span:after {
      height: 11rem;
    }
    .taxtable .a0 .b5 .c0 span:after {
      height: 24rem;
    }

    .taxtable .a1 .b2 .c0 span:after {
      height: 14.25rem;
    }
    .taxtable .a2 .b2 .c0 span:after {
      height: 11rem;
    }
  }
}
`;

const SectionsList = styled.div`
  section {
    display: block;
    margin: 100px auto 0;
    background-color: var(--color-primary-white);
    @media ${mqSmallMin} {
      margin: 115px auto 0;
    }

    > .container {
      ${WithSiteLayout};
      display: flex;
      flex-wrap: wrap;
      > * {
        width: 100%;
      }
    }
    .button-tertiary {
      margin-top: 35px;
      background: transparent;
      border: 0;
      display: flex;
      align-items: center;
      text-decoration: none;
      cursor: pointer;
      i,
      .icon {
        height: 26px;
        width: 26px;
        margin: 0 10px 0 0;
      }
    }
    .w-50 {
      width: 100%;
      max-width: 100%;
      @media ${mqLargeMin} {
        width: 50%;
      }
      h3 {
        margin-bottom: 20px;
      }
    }
    .column {
      width: 100%;
      margin-bottom: 50px;
      @media ${mqLargeMin} {
        padding: 0 100px 50px;
        width: 50%;
      }
      &:last-of-type {
        padding-bottom: 0;
        margin-bottom: 0;
      }
    }
    .column h3 {
      margin-bottom: 20px;
    }

    div[data-type='poster'] {
      .label {
        margin: var(--spacing-24) 0 0;
        line-height: 1;
        * {
          font-size: var(--font-size-300);
        }
        i {
          font-style: italic;
        }
      }
    }

    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      width: 100%;
      position: relative;
      @media ${mqSmallMin} {
        justify-content: center;
      }
      margin: 0 0 var(--spacing-48);
      border-top: 1px solid var(--outline-primary);
      position: relative;
      * {
        font-size: var(--font-size-1000);
        padding: var(--spacing-4) 0 0;
        font-weight: 300;
        margin-top: 0;
        @media ${mqLargeMin} {
          line-height: 1.5;
          font-size: var(--font-size-1600);
        }
      }
      .num {
        position: relative;
        @media ${mqSmallMin} {
          position: absolute;
          left: 0;
          top: 0;
        }
      }
    }
  }

  .s0 {
    margin: 165px 0 0;
    @media ${mqSmallMax} {
      margin-top: var(--font-size-1600);
    }
    .intro > * {
      ${WithFontBody};
      display: block;
      max-width: 100%;
      width: 760px;
      font-size: var(--font-size-400); // 30px
      font-weight: 400;
      margin: 0 0 90px 0;
      @media ${mqSmallMin} {
        margin: 0 0 145px 0;
        font-size: var(--font-size-800);
        font-weight: 300;
        line-height: var(--font-size-1000);
      }
    }
    .title {
      max-width: 100%;
      width: 400px;
      h1 {
        font-size: var(--font-size-400);
        font-weight: 500;
        margin-bottom: 22px;
      }
    }
    [data-type='button'] {
      transform: translateX(-10px);
      display: flex;
      justify-content: flex-start;
      height: 25px;
      margin: var(--spacing-48) 0 0;
      @media ${mqSmallMin} {
        margin: 27px 0 0;
        pointer-events: none;
      }
      svg {
        transform: translateY(-3px) rotate(90deg);
      }
      span {
        display: none;
      }
      a {
        display: flex;
        align-items: center;
        cursor: pointer;
      }
    }

    .w-50,
    .button-tertiary {
      max-width: 550px;
    }
  }
  .s1 {
    margin: var(--spacing-48) 0 0;
    > .container {
      display: flex;
      flex-wrap: wrap;
    }
    @media ${mqSmallMin} {
      margin: 71px 0 0;
    }
    .w-50,
    .button-tertiary {
      max-width: 550px;
    }
    .button-tertiary {
      margin-top: 35px;
    }
    .column {
      max-width: 550px;
      &:last-of-type {
        margin-bottom: 0;
        @media ${mqLargeMin} {
          margin-bottom: 50px;
        }
      }
      &.mt4 {
        margin-top: 70px;
        @media ${mqLargeMin} {
          margin-top: 50px;
        }
      }
    }
  }
  .s2 {
    .w-50 {
      max-width: 100%;
    }
  }
  .s3 {
    margin: 95px 0 0;
    @media ${mqSmallMin} {
      margin: 135px 0 0;
    }
    .column:nth-last-of-type(-n + 2) {
      border-bottom: none;
    }
    .column {
      @media ${mqLargeMin} {
        border-bottom: 1px dotted rgba(28, 58, 19, 0.2);
      }
      &:nth-child(3),
      &:nth-child(5),
      &:nth-child(7) {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      &:nth-child(6) {
        padding-bottom: 0;
        @media ${mqLargeMin} {
          margin-bottom: 0;
        }
      }
    }
    .image-flow-cytometry {
      @media ${mqLargeMin} {
        padding-top: 20px;
        padding-bottom: 70px;
      }
      > .container {
        @media ${mqLargeMin} {
          transform: scale(1.14);
        }
      }
    }
  }
  .s4 {
    @media ${mqSmallMin} {
      margin: 100px 0 0;
    }
    @media screen and (min-width: 30em) {
      .dn-ns {
        display: none;
      }
    }
    .intro * {
      ${WithFontHead};
      font-size: var(--font-size-600);

      @media ${mqSmallMin} {
        font-size: var(--font-size-800);
      }
    }
    .blurb {
      margin-bottom: 0;
      .button-tertiary {
        margin: 30px 0 0;
      }
    }
    .poster {
      margin: 55px -30px 0;

      @media ${mqSmallMin} {
        margin: 118px 0 0;
      }
      .container {
        @media ${mqSmallMin} {
          width: 100%;
          padding: 0 110px;
        }
        width: 100vw;
        overflow: auto;
      }
      .label {
        margin-top: 40px;
        @media ${mqSmallMin} {
          padding: 0 110px;
        }
      }
    }
    .swiper-pagination {
      text-align: center;
      margin-top: 35px;
    }
    .caption {
      margin-top: 20px;
      * {
        font-size: 12px;
      }
    }
    .poster img {
      max-width: none;
      max-height: 273px;
      @media ${mqSmallMin} {
        width: 100%;
        max-height: unset;
      }
    }
    .poster .swiper-slide {
      max-height: 273px;
    }
  }
  .s5 {
    margin-top: 170px;
    @media ${mqSmallMax} {
      margin-top: 100px;
    }
    .mobile {
      width: 100vw;
      margin: 0 -30px;
    }
    .w-50 {
      width: 100%;
      @media ${mqSmallMin} {
        width: calc(50% + 100px);
      }
      &:last-of-type {
        @media ${mqSmallMin} {
          width: calc(50% - 100px);
          padding-top: 4rem;
          padding-right: 100px;
        }
        h3 {
          font-size: 26px;
          font-weight: 300;
        }
      }
    }
  }
  .s6 {
    .section-title.w-50 {
      justify-content: flex-start;
      width: 50%;
      border-top: none;
      h2 {
        font-size: 50px;
      }
    }
    .content {
      padding-left: 100px;
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      margin: 0 0 80px 0;
      .col {
        &:last-child {
          @media ${mqSmallMin} {
            padding-left: 100px;
          }
        }

        @media ${mqSmallMin} {
          width: 50%;
        }
        width: 100%;
      }
      .title {
        font-size: var(--font-size-1000);
        font-weight: 300;
        @media ${mqSmallMin} {
          font-size: var(--font-size-1200);
        }
      }
      .blurb {
        @media ${mqSmallMin} {
          transform: translateY(10px);
          margin: 0;
        }
        margin: var(--spacing-24) 0 0;
      }
    }
    .grid {
      display: block;
      margin: -30px -25px 0;
      > .container {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        flex-direction: flex-start;
        width: 100%;
        padding: 0 25px;
      }
      .card {
        z-index: 1;
        margin-bottom: var(--spacing-4);

        @media ${mqSmallMin} {
          margin: 25px 0;
          width: calc(100% / 3);
          margin-bottom: var(--spacing-24);
        }
        @media ${mqLargeMin} {
          margin: 25px 0;
          width: 25%;
        }
        width: 50%;
        .text {
          display: block;
          padding-top: var(--spacing-8);
          @media ${mqSmallMin} {
            min-height: 58px;
          }
          * {
            @media ${mqSmallMin} {
              font-size: var(--font-size-400);
            }
            font-size: var(--font-size-300);
            font-weight: 400;
          }
        }
        .media {
          background-color: var(--fill-primary);
          position: relative;
          max-height: 385px;
          width: 100%;
          overflow: hidden;
        }
      }
    }
    .grid .card {
      .container {
        padding: 25px;

        @media ${mqSmallMin} {
          padding: 0 25px;
          border-right: 1px solid var(--outline-neutral);
        }
        @media ${mqSmallMax} {
          padding: 25px 15px;
        }
      }

      &:nth-last-child(2),
      &:last-child {
        .container {
          @media ${mqSmallMax} {
            padding-bottom: 0;
          }
        }
      }

      &:last-child {
        .container {
          border-right: none;
        }
      }

      &:nth-child(4n) {
        .container {
          @media ${mqLargeMin} {
            border-right: none !important;
          }
        }
      }
      &:nth-child(3n) {
        .container {
          @media ${mqSmallMin} {
            border-right: none;
          }
          @media ${mqLargeMin} {
            border-right: 1px solid var(--outline-neutral);
          }
        }
      }
    }
  }
  .s7 {
    @media ${mqSmallMin} {
      margin-top: 175px;
    }
    @media ${mqLargeMin} {
      margin-top: 195px;
    }

    .head {
      position: relative;
      display: block;
      overflow: hidden;
      width: 100%;
      margin: 0 0 var(--spacing-48);
      @media ${mqSmallMin} {
        height: 92px;
        margin: 0 0 100px;
      }
      .site-marquee {
        border: none;
        h2 {
          display: inline;
        }
      }
      * {
        ${WithFontHead};
        font-size: var(--font-size-1000);
        white-space: nowrap;
        @media ${mqSmallMin} {
          font-size: var(--font-size-1200);
        }
      }
    }
    .link {
      margin-top: 28px;
    }

    .row:nth-child(2) .col {
      margin: var(--spacing-24) 0 0;
      width: 100%;
      @media ${mqSmallMin} {
        margin: 96px 0 0;
        width: 50%;
      }
    }

    .list::-webkit-scrollbar {
      display: none;
    }

    .list {
      margin: 0 -30.5px;
      -ms-overflow-style: none;
      scrollbar-width: none;
      padding: 0;
      display: block;
      height: auto;
      overflow-x: auto;
      white-space: nowrap;
      margin-bottom: 20px;
      @media ${mqSmallMin} {
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: normal;
        max-width: 1116px;
        width: 100%;
        margin: 0 auto;
        margin-bottom: 96px;
      }
      a {
        display: inline-block;
        white-space: normal;
        @media ${mqSmallMin} {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
      }
      span {
        display: none;
        max-width: 80px;
        text-align: center;
        margin: auto;
        font-weight: 500;
        font-size: var(--font-size-400);
        @media ${mqSmallMin} {
          display: inline-block;
          max-width: 100%;
        }
      }
      img {
        margin-bottom: var(--spacing-24);
        height: 125px;
        @media ${mqSmallMin} {
          height: auto;
        }
      }
    }
  }
  .s8 {
    margin-top: var(--spacing-48);
    @media ${mqLargeMin} {
      margin-top: 100px;
    }
    .container {
      padding: 0;
      max-width: 100%;
      position: relative;
    }
    .text {
      ${WithSiteLayoutPadding};
      ${WithSiteLayoutPadding};
      transform: translate(-50%, -50%);
      align-items: flex-start;
      flex-direction: column;
      display: flex;
      position: absolute;
      width: 100%;
      max-width: 1440px;
      left: 50%;
      top: 50%;

      @media ${mqLargeMin} {
        align-items: flex-end;
      }

      > div[data-type='button'],
      p {
        width: 220px;
      }

      p {
        @media ${mqSmallMin} {
          font-size: var(--font-size-800);
          line-height: 40px;
        }
        font-size: var(--font-size-600);
        line-height: 31.2px;
        font-weight: 300;
        color: var(--text-inverse-primary);
      }

      .button {
        height: 60px;
        border-width: 1px;
        margin-top: var(--spacing-24);
        @media ${mqSmallMin} {
          height: 50px;
          max-width: 206px;
        }

        span {
          font-size: var(--font-size-350);
          text-transform: none;
          color: var(--text-inverse-primary);
        }

        &:hover,
        &:focus {
          border-color: var(--outline-light);
          background: var(--fill-primary);
          span {
            color: var(--text-primary);
          }
        }
      }
    }
  }

  [data-vimeo-initialized] {
    display: block;
    width: 100%;
  }
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: transparent;
  }
`;

const imgSci = (poster, rand) => {
  const img = poster
    ? poster[0].url.split('upload')[1]
    : `/placeholder/placeholder-${rand}.jpg`;

  return {
    width: poster?.width || 500,
    height: poster?.height || 500,
    img,
  };
};

const PageData = {
  page_type: 'default',
  page_class: 'page-approach',
  page_name: ApproachLocale.page_name,
  page_title: ApproachLocale.page_title,
  page_description: ApproachLocale.page_desc,
};

const PageHead = () => {
  const url = 'https://seed.com/approach';
  return (
    <Head>
      <title>{`${MiscLocale.brand} • ${PageData.page_title}`}</title>
      <meta name='description' content={PageData.page_description} />
      <link rel='canonical' href={url} />
    </Head>
  );
};

// TSREVIEW:
// @ts-ignore
const PageStickySM = memo(({ anchorBy, anchor, anchorsArray }) => (
  // TSREVIEW:
  // @ts-ignore
  <SiteFilter
    overlay
    title={ApproachLocale.approach}
    list={anchorsArray}
    stickyMargin={false}
    activeFilter={anchor}
    handleFilter={anchorBy}
  />
));

// TSREVIEW:
// @ts-ignore
const SectionScientists = withVisibleHoc(({ data, postList }, ref) => {
  const section = getContentSection({
    data,
    include: 'Our Scientists',
  })?.shift();

  const title = getContentField({ data: section, type: 'heading' });

  const intro = getContentField({ data: section, type: 'paragraph' });

  // TSREVIEW:
  // @ts-ignore

  const Card = memo(({ data }) => {
    const { title, subtitle, image } = data.fields;
    return (
      <div className='card'>
        <div className='container'>
          <div className='media'>
            <LegacyImage
              src={`${Url.cloudinary}/f_auto,q_auto,c_fill,g_face,w_400,ar_4:6/${image.img}`}
              width={400}
              height={600}
              alt={title || `seed ${title}`}
              loader={cloudinaryLoader}
            />
          </div>

          <div className='text'>
            <h3 className='title'>{title}</h3>
            <div className='subtitle'>{subtitle}</div>
          </div>
        </div>
      </div>
    );
  });

  // TSREVIEW:
  // @ts-ignore

  const Grid = memo(({ postList }) => (
    <section className='grid'>
      <div className='container'>
        {postList &&
          postList.length > 0 &&
          postList.map((v, i) => (
            <Card
              // TSREVIEW:
              // @ts-ignore
              data={v}
              key={`card${i}`}
            />
          ))}
      </div>
    </section>
  ));

  return (
    <section
      data-title='Scientists'
      id='section-scientists'
      // TSREVIEW:
      // @ts-ignore
      ref={ref}
      className='s6'
    >
      <div className='container'>
        <div className='row head'>
          <div className='col'>
            <div
              className='title'
              dangerouslySetInnerHTML={createMarkup(
                documentToHtmlString(title),
              )}
            />
          </div>
          <div className='col'>
            <div
              className='blurb'
              dangerouslySetInnerHTML={createMarkup(
                documentToHtmlString(intro),
              )}
            />
          </div>
        </div>
        <div className='row'>
          {/* TSREVIEW: */}
          {/* @ts-ignore */}
          <Grid postList={postList} />
        </div>
      </div>
    </section>
  );
});

// TSREVIEW:
// @ts-ignore
const SectionSeedlabs = withVisibleHoc(({ data }, ref) => {
  // TSREVIEW:
  // @ts-ignore
  const section = getContentSection({ data, include: 'Seedlabs' })?.shift();

  const title = getContentField({ data: section, type: 'heading' });

  const intro = getContentField({ data: section, type: 'paragraph' });

  const images = getContentField({ data: section, type: 'poster' }) || [];
  let arr = [];
  for (let n = 0; n < 20; n += 1) {
    arr = [...arr, n];
  }
  return (
    <section
      id='section-seedlabs'
      // TSREVIEW:
      // @ts-ignore
      ref={ref}
      className='s7'
    >
      <div className='head'>
        <SiteMarquee>
          {}
          {arr.map((v, i) => {
            // the `title` repeats so we don't want
            // to give that content repeditively to
            // screen readers, hence the `aria-hidden` logic
            return (
              // eslint-disable-next-line react/jsx-key
              <span aria-hidden={i > 0}>
                {/* TSREVIEW: */}
                {/* @ts-ignore */}
                <RichTextRenderer content={title} key='v' />
              </span>
            );
          })}
        </SiteMarquee>
      </div>

      <div className='container'>
        <div className='row'>
          <div className='list'>
            {images.map((image) => (
              <a key={image.url}>
                <div className='media'>
                  <LegacyImage
                    src={image.url}
                    width={223}
                    height={223}
                    alt={image.tags[0] || image.title}
                    loader={cloudinaryLoader}
                  />
                </div>
                <span>{image.tags[0]}</span>
              </a>
            ))}
          </div>
        </div>

        <div className='row w-50'>
          {/* TSREVIEW: */}
          {/* @ts-ignore */}
          <RichTextRenderer content={intro} />

          <Link
            href={Url.seedlabs}
            prefetch={false}
            className='button-tertiary link'
          >
            <span
              className='icon'
              dangerouslySetInnerHTML={createMarkup(Svg.arrow_circle)}
            />
            <span>{ApproachLocale.learn_about}</span>
          </Link>
        </div>
      </div>
    </section>
  );
});

type PageApproachProps = any;
type PageApproachState = any;

const PageApproach = (props: PageApproachProps) => {
  const [state, setState] = useState<PageApproachState>({
    ready: false,
    anchor: 'Overview',
    isMobileDevice: false,
  });

  const handleSectionVisible = ({ title }) => {
    setState({
      ...state,
      anchor: title,
    });
  };

  const handleAnchorBy = (v, behavior) => {
    const id = v.toLowerCase();
    if (id !== 'overview') {
      const siteHeader = document.querySelector<HTMLElement>('header');

      const siteHeaderH = siteHeader?.offsetHeight || 0;
      const target = document.querySelector(`[data-title="${v}"]`);
      const targetY = target ? getOffsetTop(target) : 0;
      const scrollLG = targetY - siteHeaderH - 90;
      const scrollSM = targetY - siteHeaderH + 16;
      const scrollTop = isScreenLarge() ? scrollLG : scrollSM;
      window.scrollTo({
        top: scrollTop,
        left: 0,
        behavior: behavior || 'smooth',
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const scrollToHash = (e?: any) => {
    const reqUrl = e?.detail?.reqURL;
    const isSci =
      window.location.hash.includes('our-scientists') ||
      (reqUrl && reqUrl.includes('our-scientists'));
    if (isSci)
      setTimeout(() => {
        handleAnchorBy('Scientists', 'auto');
      }, 0);
  };

  useEffect(() => {
    pageReplaced(PageData);

    setState({
      ...state,
      ready: true,
      isMobileDevice: window.innerWidth < 480,
    });

    scrollToHash();

    window.addEventListener('link_hash', scrollToHash);

    return () => {
      window.removeEventListener('link_hash', scrollToHash);
    };
  }, []);

  const { approach } = props;
  const scientistList = approach && approach.scientistList;

  const anchorsArray =
    approach?.section?.reduce((initArr, section) => {
      const arr = getContentFieldClass(section);
      if (arr.includes('with-anchor')) {
        return [...initArr, section.fields.title];
      }
      return initArr;
    }, []) || [];
  return (
    <>
      <GlobalStyleApproach />
      <PageHead />
      <SectionsList>
        <SiteSections
          data={approach}
          onVisible={handleSectionVisible}
          location='Approach'
          exclude={['foot', 'Our Scientists', 'Seedlabs', 'Footer-banner']}
        />

        {/* @ts-ignore */}
        <SectionScientists
          // TSREVIEW
          // @ts-ignore
          data={approach}
          postList={scientistList}
          onVisible={handleSectionVisible}
        />

        {/* @ts-ignore */}
        <SectionSeedlabs
          // TSREVIEW
          // @ts-ignore
          data={approach}
          onVisible={handleSectionVisible}
        />

        <SiteSections
          data={approach}
          onVisible={handleSectionVisible}
          location='Approach'
          // TSREVIEW:
          // Changed arg from string array to string
          // @ts-ignore
          include='Footer-banner'
        />
      </SectionsList>

      <PageStickySM
        // TSREVIEW
        // @ts-ignore
        anchorBy={handleAnchorBy}
        anchor={state.anchor}
        anchorsArray={anchorsArray}
      />

      <SitePopup id='modal-shime' type='drawer'>
        <ModalShime />
      </SitePopup>

      <SitePopup id='modal-taxonomy' type='drawer'>
        <ModalTaxonomy />
      </SitePopup>

      <SitePopup id='modal-diversity' type='drawer'>
        <ModalDiversity />
      </SitePopup>
    </>
  );
};

export async function getStaticProps() {
  const pageQuery = {
    content_type: 'page',
    'fields.slug[in]': 'approach_pds08_enabled',
    include: 10,
  };

  const pagePayload = (await getContentfulDataDirect(pageQuery)).items[0]
    .fields;
  const scientistList = (await getContentfulDataDirect(SCIENTISTS_QUERY)).items;

  type item = {
    fields: {
      image: any;
      poster: any;
    };
  };

  // @ts-ignore
  scientistList.map((v: item) => {
    const rand = Math.floor(Math.random() * 14 + 1);

    v.fields.image = imgSci(v.fields.poster, rand);
    return { ...v };
  });

  return {
    props: {
      approach: {
        // @ts-ignore because pagePayload is an object so it's wrong
        ...pagePayload,
        scientistList,
      },
      revalidate: 60 * 60, // One hour in seconds
    },
  };
}

export default PageApproach;
